import type { Pinia } from 'pinia';
import type { App } from 'vue';
import type { Router } from 'vue-router';

import * as Sentry from '@sentry/vue';
import type { BrowserOptions } from '@sentry/vue';

export type Environment = 'dev' | 'rec' | 'prd';

const isUserSampled = Math.random() < 0.1;

const getSentryOptions = ({
  app,
  router,
  pinia,
  dsnSentry,
  env,
  networkDetailAllowUrls,
}: {
  app: App<Element>;
  router?: Router;
  pinia?: Pinia;
  dsnSentry?: string;
  env?: Environment;
  networkDetailAllowUrls?: Array<string>;
}) => {
  const integrations = [
    Sentry.browserProfilingIntegration(),
    Sentry.vueIntegration({
      tracingOptions: {
        trackComponents: true,
        hooks: ['mount', 'update', 'unmount'],
      },
    }),
    Sentry.browserTracingIntegration({ router }),
    ...(networkDetailAllowUrls && networkDetailAllowUrls.length > 0
      ? [
          Sentry.replayIntegration({
            networkDetailAllowUrls: networkDetailAllowUrls.map(url => {
              return new RegExp(url);
            }),
            networkRequestHeaders: ['X-Custom-Header'],
            networkResponseHeaders: ['X-Custom-Header'],
          }),
        ]
      : []),
  ];

  if (pinia) {
    pinia.use(Sentry.createSentryPiniaPlugin());
  }

  let sentryOptions = {
    app,
    environment: env || 'unknown',
    dsn: dsnSentry,
    normalizeDepth: 10,
    integrations,
    // Tracing
    tracesSampler: samplingContext => samplingContext.inheritOrSampleWith(isUserSampled ? 1 : 0),
    // Session Replay
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1,
    // Profiling
    profileSessionSampleRate: isUserSampled ? 1 : 0,
    profileLifecycle: 'trace',
  } as BrowserOptions;

  if (__APP_VERSION__) {
    sentryOptions = {
      ...sentryOptions,
      release: __APP_VERSION__,
    };
  }

  return sentryOptions;
};

export { getSentryOptions };
